import { createStore } from './store-factory';
import { TransformedTreeNode, TreeNodeData } from '../components/NetPPM/TreeTable/types';

interface TreeSelectionState {
  selectedNodes: Map<string, TransformedTreeNode>;
  originalTreeData: TreeNodeData | null;
  toggleSelection: (node: TransformedTreeNode) => void;
  removeSelection: (key: string) => void;
  clearSelections: () => void;
  getSelectedNodes: () => TransformedTreeNode[];
  setOriginalTreeData: (data: TreeNodeData) => void;
  getTreeForAPI: () => TreeNodeData | null;
}

// Helper function to generate node key (same logic as in treeTransformers)
// Uses URL encoding to preserve uniqueness and prevent collisions
const generateNodeKey = (name: string, parentKey: string = ''): string => {
  const encodedName = encodeURIComponent(name);
  return parentKey ? `${parentKey}:${encodedName}` : encodedName;
};

// Helper function to recursively mark selections in tree
const markSelections = (
  node: TreeNodeData,
  selectedKeys: Set<string>,
  parentKey: string = ''
): TreeNodeData => {
  const nodeKey = generateNodeKey(node.name, parentKey);
  const isSelected = selectedKeys.has(nodeKey);
  
  const markedNode: TreeNodeData = {
    ...node,
    selected: isSelected,
  };

  if (node.children && node.children.length > 0) {
    markedNode.children = node.children.map(child =>
      markSelections(child, selectedKeys, nodeKey)
    );
  }

  return markedNode;
};

// Helper function to collect all descendant keys from a node
const collectDescendantKeys = (node: TransformedTreeNode, keys: Set<string> = new Set()): Set<string> => {
  if (node.children) {
    node.children.forEach(child => {
      keys.add(child.key);
      collectDescendantKeys(child, keys);
    });
  }
  return keys;
};

export const useTreeSelectionStore = createStore<TreeSelectionState>(
  'tree-selection',
  (set, get) => ({
    selectedNodes: new Map(),
    originalTreeData: null,

    setOriginalTreeData: (data: TreeNodeData) => {
      set({ originalTreeData: data });
    },

    toggleSelection: (node: TransformedTreeNode) => {
      if (!node.isSelectable) return;

      set((state) => {
        const newSelectedNodes = new Map(state.selectedNodes);
        const wasSelected = newSelectedNodes.has(node.key);
        
        if (wasSelected) {
          newSelectedNodes.delete(node.key);
          // Remove all descendants when deselecting parent
          collectDescendantKeys(node).forEach(key => newSelectedNodes.delete(key));
        } else {
          newSelectedNodes.set(node.key, node);
        }

        return { selectedNodes: newSelectedNodes };
      });
    },

    removeSelection: (key: string) => {
      set((state) => {
        const newSelectedNodes = new Map(state.selectedNodes);
        newSelectedNodes.delete(key);
        return { selectedNodes: newSelectedNodes };
      });
    },

    clearSelections: () => {
      set({ selectedNodes: new Map() });
    },

    getSelectedNodes: () => {
      return Array.from(get().selectedNodes.values());
    },

    getTreeForAPI: () => {
      const { originalTreeData, selectedNodes } = get();
      
      if (!originalTreeData) {
        return null;
      }

      const selectedKeys = new Set(selectedNodes.keys());
      const treeWithSelections = markSelections(originalTreeData, selectedKeys);
      
      return treeWithSelections;
    },
  })
);
